import {
  Button,
  DatePickerFieldRhf,
  Form,
  InputFieldRhf,
  SizableText,
  TimePickerFieldRhf,
  XStack,
  YStack,
} from '@my/ui'
import { UserAvatar } from 'app/features/user/components'
import { ChevronDown } from '@tamagui/lucide-icons'
import { Group, User } from 'app/types'
import { PostCreationHeader } from '../post-creation-header'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { KeyboardAwareScrollView } from 'app/components/layout'
import { concatDateAndTime } from '../helpers'

type AnnouncementCreationViewProps = {
  title: string
  user: Pick<User, 'fullName' | 'avatar'>
  selectedGroup?: Pick<Group, 'id' | 'name'> | null
  onBack?: () => void
  defaultValues?: AnnouncementFormData
  onOpenChange: (open: boolean) => void
  onGroupSelectPress?: () => void
  onSubmit: (data: SubmitAnnouncementData, cb: () => void) => void
  isSubmitting?: boolean
  isEditing?: boolean
}

const formSchema = z
  .object({
    title: z.string().min(1).max(100, "Title can't be longer than 100 characters"),
    body: z.string().min(1).max(1000, "Body can't be longer than 1000 characters").optional(),
    scheduledDate: z.string().optional(),
    scheduledTime: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.scheduledDate && !data.scheduledTime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Scheduled time is required',
        path: ['scheduledTime'],
      })
    }

    if (!data.scheduledDate && data.scheduledTime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Scheduled date is required',
        path: ['scheduledDate'],
      })
    }
    if (data.scheduledDate && data.scheduledTime) {
      const scheduledDateTime = concatDateAndTime(new Date(data.scheduledDate), data.scheduledTime)
      if (scheduledDateTime < new Date()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Scheduled date and time must be in the future',
          path: ['scheduledDate'],
        })
      }
    }
  })

export type AnnouncementFormData = z.infer<typeof formSchema>

export interface SubmitAnnouncementData extends AnnouncementFormData {
  group: Pick<Group, 'id' | 'name'>
}

export const AnnouncementCreationView = ({
  title,
  user,
  onBack,
  selectedGroup,
  onOpenChange,
  onGroupSelectPress,
  defaultValues,
  onSubmit,
  isSubmitting,
  isEditing,
}: AnnouncementCreationViewProps) => {
  const { control, handleSubmit, reset } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...defaultValues,
    },
  })

  const canPost = !!selectedGroup?.id

  const handleSubmitAnnouncement = async (data: AnnouncementFormData) => {
    if (!selectedGroup || !canPost) {
      return
    }

    onSubmit(
      {
        group: selectedGroup,
        ...data,
      },
      () => {
        reset()
      }
    )
  }

  return (
    <YStack flex={1}>
      <Form gap="$3.5" flex={1} onSubmit={handleSubmit(handleSubmitAnnouncement)}>
        {/* Header */}
        <PostCreationHeader
          title={title}
          onBack={onBack}
          {...(!onBack && { onClose: () => onOpenChange(false) })}
        >
          <Form.Trigger asChild ai="center" fd="row">
            <Button
              size="$3"
              theme="primary"
              disabled={!canPost}
              opacity={canPost ? 1 : 0.5}
              loading={isSubmitting}
              $gtSm={{
                size: '$4',
              }}
            >
              Publish
            </Button>
          </Form.Trigger>
        </PostCreationHeader>
        <KeyboardAwareScrollView style={{ flex: 1 }}>
          <YStack flex={1} gap="$3.5">
            <XStack w="100%" gap="$3.5" ai="flex-start">
              <UserAvatar src={user?.avatar?.small} width={44} height={44} />
              <YStack flex={1} gap="$1">
                <SizableText size="$3" fow="500">
                  {user?.fullName}
                </SizableText>
                <XStack gap="$2" flexWrap="wrap">
                  {onGroupSelectPress && (
                    <Button
                      theme="gray"
                      size="$1"
                      px="$1.5"
                      onPress={onGroupSelectPress}
                      iconAfter={<ChevronDown size={12} />}
                    >
                      <SizableText size="$2" fow="500" numberOfLines={1} maxWidth={100}>
                        {selectedGroup ? selectedGroup.name : 'Select Group'}
                      </SizableText>
                    </Button>
                  )}
                  {!selectedGroup && !isEditing && (
                    <SizableText size="$1">Select a group to publish</SizableText>
                  )}
                </XStack>
              </YStack>
            </XStack>

            <InputFieldRhf
              name="title"
              control={control}
              label="Announcement Title"
              placeholder="Announcement Title"
              required
              fullWidth
              shadowOpacity={0}
              showHelperText
              boc="$gray4"
              hoverStyle={{
                boc: '$gray6',
              }}
              focusStyle={{
                boc: '$gray7',
                outlineWidth: 0,
              }}
            />

            <InputFieldRhf
              name="body"
              control={control}
              label="Announcement body"
              placeholder="Announcement body"
              fullWidth
              shadowOpacity={0}
              multiline
              rows={4}
              showHelperText
              boc="$gray4"
              minHeight={100}
              hoverStyle={{
                boc: '$gray6',
              }}
              focusStyle={{
                boc: '$gray7',
                outlineWidth: 0,
              }}
            />

            <DatePickerFieldRhf
              name="scheduledDate"
              control={control}
              label="Scheduled Date (Optional)"
              placeholder="Scheduled Date"
              fullWidth
              showHelperText
              containerProps={{
                flex: 1,
                flexBasis: '49%',
              }}
              popoverProps={{
                placement: 'top',
              }}
              inputProps={{
                backgroundColor: '$white',
              }}
            />

            <TimePickerFieldRhf
              name="scheduledTime"
              control={control}
              label="Scheduled Time (Optional)"
              placeholder="Scheduled Time"
              fullWidth
              showHelperText
              containerProps={{
                flex: 1,
                flexBasis: '49%',
              }}
              popoverProps={{
                placement: 'top',
              }}
              inputProps={{
                backgroundColor: '$white',
              }}
            />
          </YStack>
        </KeyboardAwareScrollView>
      </Form>
    </YStack>
  )
}
